import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceAnalytics = ({ performanceData, onExportResults }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeframe, setTimeframe] = useState('7d');

  const equityCurveData = [
    { date: '2025-01-01', value: 10000, trades: 0 },
    { date: '2025-01-02', value: 10250, trades: 3 },
    { date: '2025-01-03', value: 10180, trades: 5 },
    { date: '2025-01-04', value: 10420, trades: 8 },
    { date: '2025-01-05', value: 10380, trades: 12 },
    { date: '2025-01-06', value: 10650, trades: 15 },
    { date: '2025-01-07', value: 10580, trades: 18 },
    { date: '2025-01-08', value: 10750, trades: 22 },
    { date: '2025-01-09', value: 10920, trades: 25 },
    { date: '2025-01-10', value: 10850, trades: 28 },
    { date: '2025-01-11', value: 11100, trades: 32 }
  ];

  const dailyReturnsData = [
    { date: '01/01', return: 0 },
    { date: '01/02', return: 2.5 },
    { date: '01/03', return: -0.7 },
    { date: '01/04', return: 2.4 },
    { date: '01/05', return: -0.4 },
    { date: '01/06', return: 2.7 },
    { date: '01/07', return: -0.7 },
    { date: '01/08', return: 1.7 },
    { date: '01/09', return: 1.7 },
    { date: '01/10', return: -0.7 },
    { date: '01/11', return: 2.9 }
  ];

  const metrics = {
    totalReturn: 11.0,
    sharpeRatio: 1.85,
    maxDrawdown: -3.2,
    winRate: 68.5,
    profitFactor: 2.34,
    avgWin: 145.50,
    avgLoss: -62.30,
    totalTrades: 32,
    winningTrades: 22,
    losingTrades: 10
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    // { id: 'equity', label: 'Equity Curve', icon: 'TrendingUp' },
    // { id: 'returns', label: 'Daily Returns', icon: 'Activity' },
    // { id: 'metrics', label: 'Risk Metrics', icon: 'Shield' }
  ];

  const timeframes = [
    { id: '1d', label: '1D' },
    { id: '7d', label: '7D' },
    { id: '30d', label: '30D' },
    { id: 'all', label: 'All' }
  ];

  const formatTooltipValue = (value, name) => {
    if (name === 'value') return [`$${value?.toLocaleString()}`, 'Portfolio Value'];
    if (name === 'return') return [`${value}%`, 'Daily Return'];
    return [value, name];
  };

  return (
    <div className="bg-card border border-border rounded-2xl trading-shadow">
      <div className="p-6 border-b border-border-secondary">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Performance Analytics</h3>
              <p className="text-sm text-muted-foreground">Paper trading results and metrics</p>
            </div>
          </div>
          {/* <div className="flex flex-col lg:flex-row items-center space-x-2">
            <div className="flex bg-muted rounded-lg p-1">
              {timeframes?.map((tf) => (
                <button
                  key={tf?.id}
                  onClick={() => setTimeframe(tf?.id)}
                  className={`px-3 py-1 text-sm font-medium rounded-md trading-transition ${
                    timeframe === tf?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tf?.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onExportResults}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
          </div> */}
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border-secondary">
        <div className="grid grid-cols-2 lg:grid-cols-4 space-x-1 p-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium trading-transition ${
                activeTab === tab?.id
                  ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-success">+{metrics?.totalReturn}%</p>
                <p className="text-sm text-muted-foreground">Total Return</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{metrics?.sharpeRatio}</p>
                <p className="text-sm text-muted-foreground">Sharpe Ratio</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-error">{metrics?.maxDrawdown}%</p>
                <p className="text-sm text-muted-foreground">Max Drawdown</p>
              </div>
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-primary">{metrics?.winRate}%</p>
                <p className="text-sm text-muted-foreground">Win Rate</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Trading Statistics</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Trades</span>
                    <span className="font-medium text-foreground">{metrics?.totalTrades}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Winning Trades</span>
                    <span className="font-medium text-success">{metrics?.winningTrades}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Losing Trades</span>
                    <span className="font-medium text-error">{metrics?.losingTrades}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Profit Factor</span>
                    <span className="font-medium text-foreground">{metrics?.profitFactor}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Average P&L</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Average Win</span>
                    <span className="font-medium text-success">+${metrics?.avgWin}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Average Loss</span>
                    <span className="font-medium text-error">${metrics?.avgLoss}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Risk/Reward</span>
                    <span className="font-medium text-foreground">1:{(metrics?.avgWin / Math.abs(metrics?.avgLoss))?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Equity Curve Tab */}
        {activeTab === 'equity' && (
          <div className="space-y-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={equityCurveData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    fontSize={12}
                    tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    fontSize={12}
                    tickFormatter={(value) => `$${(value / 1000)?.toFixed(1)}k`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(15, 15, 15, 0.95)',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      color: '#ffffff'
                    }}
                    formatter={formatTooltipValue}
                    labelFormatter={(value) => new Date(value)?.toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#FFD700" 
                    strokeWidth={2}
                    dot={{ fill: '#FFD700', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#FFD700', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Daily Returns Tab */}
        {activeTab === 'returns' && (
          <div className="space-y-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyReturnsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255, 255, 255, 0.5)"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="rgba(255, 255, 255, 0.5)"
                    fontSize={12}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(15, 15, 15, 0.95)',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      color: '#ffffff'
                    }}
                    formatter={formatTooltipValue}
                  />
                  <Bar 
                    dataKey="return" 
                    fill={(entry) => entry?.return >= 0 ? '#00FF88' : '#FF4444'}
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Risk Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Risk Analysis</h4>
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Value at Risk (95%)</span>
                    <span className="font-medium text-error">-$234</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-error h-2 rounded-full" style={{ width: '23%' }} />
                  </div>
                </div>

                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Beta</span>
                    <span className="font-medium text-foreground">0.85</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full" style={{ width: '85%' }} />
                  </div>
                </div>

                <div className="bg-muted/30 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-muted-foreground">Volatility</span>
                    <span className="font-medium text-warning">12.4%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-warning h-2 rounded-full" style={{ width: '62%' }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Performance Ratios</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Calmar Ratio</span>
                  <span className="font-medium text-foreground">3.44</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sortino Ratio</span>
                  <span className="font-medium text-foreground">2.18</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Information Ratio</span>
                  <span className="font-medium text-foreground">1.92</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Treynor Ratio</span>
                  <span className="font-medium text-foreground">0.129</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Alpha</span>
                  <span className="font-medium text-success">+2.3%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceAnalytics;